import { ESdChangeReason, IWorkFlow } from "@kuika/kuika-cl-model";
import { EWFDueUnit, EWFEventTag, EWFEventType, IWFBoundaryEvent } from "@kuika/kuika-cl-model/src/models/workflow";
import _ from "lodash";
import type { ChangeEvent } from "react";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaCheckbox from "../../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import { WorkflowDue } from "../wf-utils/workflow-due";
import "./workflow-boundary-event-properties.scss";

export interface IWorkFlowBoundaryEventPropertiesState {
  boundaryEvent: IWFBoundaryEvent | undefined;
  loading: boolean;
  // timerDisabled: boolean;
}

export interface IWorkFlowBoundaryEventPropertiesProps {
  selectedNodeId: string | undefined;
  eventTag: EWFEventTag | undefined;
}

class WorkFlowBoundaryEventProperties extends PureSdComponent<
  IWorkFlowBoundaryEventPropertiesProps & IWithSDProps & IWithContextProp,
  IWorkFlowBoundaryEventPropertiesState
> {
  constructor(props: IWorkFlowBoundaryEventPropertiesProps & IWithSDProps & IWithContextProp) {
    super(props);
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const eventStep = this.props.sd.findStepRecursively(
      wf!,
      (step) => step.boundaryEvent?.id === this.props.selectedNodeId
    );
    const selectedEvent = _.cloneDeep(eventStep?.boundaryEvent);
    if (selectedEvent?.timerEventDue == undefined) {
      selectedEvent!.timerEventDue = { dueUnit: EWFDueUnit.Days, dueValue: 1 };
    }
    this.state = { boundaryEvent: selectedEvent, loading: false };
  }

  getEventTagText = (eventTag: EWFEventTag | undefined) => {
    switch (eventTag) {
      case EWFEventTag.Timer:
        return "Timer";
      case EWFEventTag.Message:
        return "Message";
      case EWFEventTag.Signal:
        return "Signal";
      case EWFEventTag.Undefined:
      default:
        return "";
    }
  };

  toggleTimerCopy = async (event) => {
    const { boundaryEvent } = this.state;
    this.setState({ loading: true });
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const eventStep = this.props.sd.findStepRecursively(
      wf!,
      (step) => step.boundaryEvent?.id === this.props.selectedNodeId
    );
    if (event) {
      boundaryEvent!.timerEventDue = eventStep?.dueIn;
    }
    boundaryEvent!.useStepTimer = event;
    eventStep!.boundaryEvent = boundaryEvent;
    await this.props.sd.saveWorkFlow(wf!);
    this.setState({ boundaryEvent, loading: false });
  };

  eventInterruptionChangeHandler = async (e) => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const selectedEventStep = this.props.sd.findStepRecursively(
      wf!,
      (step) => step.boundaryEvent?.id === this.props.selectedNodeId
    );
    const selectedEvent = selectedEventStep?.boundaryEvent;
    if (wf && selectedEvent && selectedEvent.eventType == EWFEventType.Boundary) {
      selectedEvent.isInterrupting = e.target.checked;
      const values = [{ name: "cancelActivity", value: e.target.checked }];
      this.props.sd.updateDiagramElement(selectedEvent.id, values);

      this.setState({ boundaryEvent: { ...selectedEvent } }, async () => {
        await this.props.sd.saveWorkFlow(wf);
      });
    }
  };

  dueChangeHandler = async (value, type) => {
    const { boundaryEvent } = this.state;
    if (type === "unit") {
      boundaryEvent!.timerEventDue!.dueUnit = value as EWFDueUnit;
    } else if (type === "value") {
      boundaryEvent!.timerEventDue!.dueValue = value;
    }
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const eventStep = this.props.sd.findStepRecursively(
      wf!,
      (step) => step.boundaryEvent?.id === this.props.selectedNodeId
    );
    const selectedEvent = eventStep?.boundaryEvent;
    if (
      wf &&
      selectedEvent &&
      selectedEvent.eventType == EWFEventType.Boundary &&
      selectedEvent.eventTag == EWFEventTag.Timer
    ) {
      selectedEvent.timerEventDue = boundaryEvent!.timerEventDue;
      await this.props.sd.saveWorkFlow(wf);
      this.setState({ boundaryEvent });
    }
  };

  render() {
    const { boundaryEvent } = this.state;
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const step = this.props.sd.findStepRecursively(wf!, (step) => step.boundaryEvent?.id === this.props.selectedNodeId);

    if (!step) {
      return (
        <div className="kuika__workflow__fields__header" style={{ border: "none" }}>
          <div className="kuika__workflow__fields__header-top">
            <span className="kuika_workflow-properties__property-type">Boundary Event Not Found</span>
          </div>
          <KuikaSmallText className="kuika-wf-boundary-event__hint">
            No step found with boundary event ID: {this.props.selectedNodeId}
          </KuikaSmallText>
        </div>
      );
    }

    const event = step.boundaryEvent;
    return (
      <>
        <div className="kuika__workflow__fields__header" style={{ border: "none" }}>
          <div className="kuika__workflow__fields__header-top">
            <span className="kuika_workflow-properties__property-type">
              {this.getEventTagText(event?.eventTag)} Boundary Event
            </span>
          </div>
        </div>
        <div
          style={{
            height: "calc(100vh - 456px)",
            overflowY: "scroll",
            overflowX: "hidden"
          }}
        >
          <div className="kuika_workflow-properties__section">
            <div className="kuika-wf-boundary-event__fields">
              <KuikaCheckbox
                label="Is Interrupting"
                checked={event?.isInterrupting === true}
                onChange={(e: ChangeEvent<HTMLInputElement>) => this.eventInterruptionChangeHandler(e)}
              />
              <div className="kuika-wf-boundary-event__timer">
                <div className="kuika-wf-boundary-event__timer-header">
                  <KuikaText>Timer Duration</KuikaText>
                  <KuikaTooltip content="Use Step Due">
                    {/* Radix clones the trigger and hands it a ref; KuikaSwitch is a plain
                        function component, so the tooltip anchors on this span instead. */}
                    <span className="kuika-wf-boundary-event__timer-toggle">
                      <KuikaSwitch
                        labelPosition="none"
                        onChange={(val: boolean) => this.toggleTimerCopy(val)}
                        checked={boundaryEvent?.useStepTimer === true}
                        disabled={this.state.loading}
                      />
                    </span>
                  </KuikaTooltip>
                </div>
                <WorkflowDue
                  key={boundaryEvent?.id}
                  due={boundaryEvent?.timerEventDue}
                  onDueUnitChange={(val) => this.dueChangeHandler(val, "unit")}
                  onDueValueChange={(val) => this.dueChangeHandler(val, "value")}
                  loading={boundaryEvent!.useStepTimer}
                  id="boundary-event-due"
                ></WorkflowDue>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (changeReason === ESdChangeReason.SaveWorkflow) {
      return true;
    }
    return false;
  };
}

const workFlowBoundaryEventProperties = withContext<IWorkFlowBoundaryEventPropertiesProps>(
  withSD<IWorkFlowBoundaryEventPropertiesProps>(WorkFlowBoundaryEventProperties)
);
export { workFlowBoundaryEventProperties as WorkFlowBoundaryEventProperties };
